import { createContext, memo, useContext, useMemo, type JSX } from 'react';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import { LayersIcon, Pencil2Icon, PlusIcon } from '@radix-ui/react-icons';
import type { PropertyListSorting, PropertySortColumn } from '@/lib/types/property';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import type { PropertySummary } from '@/lib/types/property';
import {
  formatAvailabilityRange,
  formatPropertyAddress,
  formatRentRange,
  formatSquareFeet,
} from '@/lib/utils/propertyFormatting';
import { formatAssetTypeLabel } from '@/lib/constants/propertyOptions';
import {
  PropertyBulkActionsBar,
  PropertyDeleteButton,
  usePropertySelection,
} from '@/components/dashboard/properties';
import {
  PropertyActionIconButton,
  propertyActionIconClassName,
} from '@/components/dashboard/properties/PropertyActionIconButton';
import { useDashboardPropertyRow } from '@/hooks/useDashboardProperties';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';

export type PropertyOverviewLayout = 'desktop' | 'tablet' | 'mobile';
export type PropertyTableDensity = 'comfortable' | 'compact';

type PropertySummaryMap = Map<string, PropertySummary>;

const PropertyListContext = createContext<PropertySummaryMap | null>(null);

function usePropertySummarySelector(propertyId: string): PropertySummary {
  const map = useContext(PropertyListContext);
  if (!map) {
    throw new Error('usePropertySummarySelector must be used within PropertyListContext');
  }
  const summary = map.get(propertyId);
  if (!summary) {
    throw new Error(`Property summary not found for id ${propertyId}`);
  }
  return summary;
}

interface SortableHeaderProps {
  label: string;
  column: PropertySortColumn;
  sort?: PropertyListSorting;
  onSortChange?: (column: PropertySortColumn) => void;
}

function SortableHeader({ label, column, sort, onSortChange }: SortableHeaderProps): JSX.Element {
  const isActive = sort?.column === column;
  const direction = isActive ? sort.direction : undefined;
  const ariaSort = isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none';
  const indicator = isActive ? (direction === 'asc' ? '↑' : '↓') : '↕';

  return (
    <th
      scope="col"
      className="px-4 py-3 text-left font-semibold"
      aria-sort={ariaSort}
      data-sort-column={column}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 text-xs uppercase tracking-wide text-text-primary/70 transition hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        onClick={() => {
          onSortChange?.(column);
        }}
      >
        <span>{label}</span>
        <span aria-hidden="true" className="text-base leading-none">
          {indicator}
        </span>
      </button>
    </th>
  );
}

const updatedFormatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatUpdatedAt(value: string): string {
  if (!value) {
    return '';
  }
  return updatedFormatter.format(new Date(value));
}
type PropertyListPaginationSummary = {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
};

interface PropertyOverviewListProps {
  properties: readonly PropertySummary[];
  layout: PropertyOverviewLayout;
  pagination?: PropertyListPaginationSummary;
  sort?: PropertyListSorting;
  onSortChange?: (column: PropertySortColumn) => void;
  density?: PropertyTableDensity;
}

export function PropertyOverviewList({
  properties,
  layout,
  pagination,
  sort,
  onSortChange,
  density = 'comfortable',
}: PropertyOverviewListProps): JSX.Element {
  const propertyMap = useMemo<PropertySummaryMap>(() => {
    return new Map(properties.map((property) => [property.id, property]));
  }, [properties]);

  const propertyIds = useMemo(() => properties.map((property) => property.id), [properties]);

  if (properties.length === 0) {
    return (
      <div className="bg-surface-muted/40 rounded-3xl border border-dashed border-surface-subtle p-8 text-center text-sm text-text-primary/70">
        No properties match the current filters. Publish a property or adjust filters to continue.
      </div>
    );
  }

  if (layout === 'mobile') {
    return (
      <PropertyListContext.Provider value={propertyMap}>
        <div className="flex flex-col gap-4">
          {propertyIds.map((propertyId) => (
            <MemoizedPropertyCard key={propertyId} propertyId={propertyId} />
          ))}
        </div>
      </PropertyListContext.Provider>
    );
  }

  return (
    <PropertyListContext.Provider value={propertyMap}>
      <div className="overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-sm">
        <div className="border-b border-surface-subtle px-4 py-3">
          <PropertyBulkActionsBar pagination={pagination} />
        </div>
        <div className="overflow-x-auto">
          <table
            className="divide-surface-muted min-w-full divide-y text-sm text-text-primary"
            data-table-density={density}
          >
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wide text-text-primary/60">
              <tr>
                <th scope="col" className="w-12 px-4 py-3 text-left">
                  <ListHeaderCheckbox properties={properties} />
                </th>
                <SortableHeader
                  label="Property"
                  column="propertyCode"
                  sort={sort}
                  onSortChange={onSortChange}
                />
                <SortableHeader
                  label="Rentable SF"
                  column="totalRentableSf"
                  sort={sort}
                  onSortChange={onSortChange}
                />
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Units shown on website
                </th>
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Net / SF
                </th>
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Additional / SF
                </th>
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Availability
                </th>
                <SortableHeader
                  label="Updated"
                  column="updatedAt"
                  sort={sort}
                  onSortChange={onSortChange}
                />
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Visibility
                </th>
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-surface-muted/80 divide-y">
              {propertyIds.map((propertyId) => (
                <MemoizedPropertyRow key={propertyId} propertyId={propertyId} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PropertyListContext.Provider>
  );
}

function ListHeaderCheckbox({
  properties,
}: {
  properties: readonly PropertySummary[];
}): JSX.Element {
  const selection = usePropertySelection();
  const allSelected =
    properties.length > 0 && properties.every((property) => selection.isSelected(property.id));
  const someSelected =
    !allSelected && properties.some((property) => selection.isSelected(property.id));

  return (
    <input
      type="checkbox"
      aria-label="Select all properties"
      className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
      checked={allSelected}
      ref={(input) => {
        if (input) {
          input.indeterminate = someSelected;
        }
      }}
      onChange={(event) => {
        if (event.target.checked) {
          selection.selectProperties(properties);
        } else {
          selection.clearSelection();
        }
      }}
    />
  );
}

function RowSelectionCheckbox({ property }: { property: PropertySummary }): JSX.Element {
  const selection = usePropertySelection();

  return (
    <input
      type="checkbox"
      aria-label={`Select ${formatPropertyAddress(property.address)}`}
      className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
      checked={selection.isSelected(property.id)}
      onChange={(event) => {
        selection.toggleProperty(property, event.target.checked);
      }}
    />
  );
}

function RowLoadingOverlay(): JSX.Element {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm"
      data-testid="row-loading-indicator"
      aria-label="Refreshing row"
    >
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand-primary/40 border-t-brand-primary" />
    </div>
  );
}

function PropertyRow({ propertyId }: { propertyId: string }): JSX.Element {
  const user = useDashboardUser();
  const navigate = useNavigate();
  const baseSummary = usePropertySummarySelector(propertyId);
  const {
    data: rowData,
    isFetching,
    isLoading,
  } = useDashboardPropertyRow({
    user,
    property: baseSummary,
  });
  const summary = rowData ?? baseSummary;
  const isRefreshing = isFetching && !isLoading;
  const propertyPath = `/dashboard/properties/${summary.id}`;

  return (
    <tr
      className={clsx(
        'hover:bg-surface-muted/30 relative cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40',
      )}
      aria-busy={isRefreshing}
      data-row-refreshing={isRefreshing ? 'true' : 'false'}
      role="link"
      tabIndex={0}
      aria-label={`Open ${formatPropertyAddress(summary.address)}`}
      onClick={(event) => {
        if (event.target instanceof Element) {
          const interactive = event.target.closest('a,button,input,select,textarea,label');
          if (interactive) {
            return;
          }
        }
        navigate(propertyPath);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        if (event.target instanceof Element) {
          const interactive = event.target.closest('a,button,input,select,textarea,label');
          if (interactive) {
            return;
          }
        }
        event.preventDefault();
        navigate(propertyPath);
      }}
    >
      <td className="px-4 py-4 align-top">
        {isRefreshing ? <RowLoadingOverlay /> : null}
        <RowSelectionCheckbox property={summary} />
      </td>
      <td className="px-4 py-4 align-top">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-primary">
            {formatPropertyAddress(summary.address)}
          </p>
          <p className="text-xs text-text-primary/70">
            {summary.submarket} • {formatAssetTypeLabel(summary.assetType)}
          </p>
        </div>
      </td>
      <td className="px-4 py-4 align-top">{formatSquareFeet(summary.totalRentableSf)}</td>
      <td className="px-4 py-4 align-top">{summary.publishedUnitCount}</td>
      <td className="px-4 py-4 align-top">
        {formatRentRange(summary.minNetRent, summary.maxNetRent)}
      </td>
      <td className="px-4 py-4 align-top">
        {formatRentRange(summary.minAdditionalRent, summary.maxAdditionalRent)}
      </td>
      <td className="px-4 py-4 align-top">
        {formatAvailabilityRange(summary.earliestAvailability, summary.latestAvailability)}
      </td>
      <td className="px-4 py-4 align-top text-xs text-text-primary/70">
        {formatUpdatedAt(summary.updatedAt)}
      </td>
      <td className="px-4 py-4 align-top">
        <StatusBadge
          tone={summary.hasVacancy ? 'success' : 'neutral'}
          label={summary.hasVacancy ? 'Vacancy' : 'No vacancy'}
        />
      </td>
      <td className="px-4 py-4 align-top">
        <div className="flex flex-wrap gap-2">
          <OpenPropertyLink propertyId={summary.id} mode="icon" />
          <AddUnitLink propertyId={summary.id} mode="icon" />
          <EditPropertyLink propertyId={summary.id} mode="icon" />
          <PropertyDeleteButton property={summary} mode="icon" />
        </div>
      </td>
    </tr>
  );
}

const MemoizedPropertyRow = memo(PropertyRow);

function PropertyCard({ propertyId }: { propertyId: string }): JSX.Element {
  const user = useDashboardUser();
  const baseSummary = usePropertySummarySelector(propertyId);
  const {
    data: rowData,
    isFetching,
    isLoading,
  } = useDashboardPropertyRow({
    user,
    property: baseSummary,
  });
  const summary = rowData ?? baseSummary;
  const isRefreshing = isFetching && !isLoading;

  return (
    <article className="relative rounded-2xl border border-surface-subtle bg-white p-4 shadow-sm">
      {isRefreshing ? <RowLoadingOverlay /> : null}
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {summary.submarket} • {formatAssetTypeLabel(summary.assetType)}
        </p>
        <h3 className="text-lg font-semibold text-text-primary">
          {formatPropertyAddress(summary.address)}
        </h3>
        <p className="text-xs text-text-primary/60">
          {summary.hasVacancy ? 'Vacancy available' : 'No vacancy right now'}
        </p>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-xs text-text-primary/70">
        <div>
          <dt className="uppercase tracking-wide">Rentable SF</dt>
          <dd className="text-sm font-semibold text-text-primary">
            {formatSquareFeet(summary.totalRentableSf)}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wide">Units shown on website</dt>
          <dd className="text-sm font-semibold text-text-primary">{summary.publishedUnitCount}</dd>
        </div>
        <div>
          <dt className="uppercase tracking-wide">Net / SF</dt>
          <dd className="text-sm font-semibold text-text-primary">
            {formatRentRange(summary.minNetRent, summary.maxNetRent)}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wide">Additional / SF</dt>
          <dd className="text-sm font-semibold text-text-primary">
            {formatRentRange(summary.minAdditionalRent, summary.maxAdditionalRent)}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wide">Availability</dt>
          <dd className="text-sm font-semibold text-text-primary">
            {formatAvailabilityRange(summary.earliestAvailability, summary.latestAvailability)}
          </dd>
        </div>
      </dl>
      <div className="mt-4 flex flex-col gap-2">
        <OpenPropertyLink propertyId={summary.id} className="w-full" />
        <AddUnitLink propertyId={summary.id} className="w-full" variant="secondary" size="sm" />
        <EditPropertyLink
          propertyId={summary.id}
          className="w-full"
          variant="secondary"
          size="sm"
        />
        <PropertyDeleteButton property={summary} fullWidth size="sm" variant="secondary" />
      </div>
    </article>
  );
}

const MemoizedPropertyCard = memo(PropertyCard);

function OpenPropertyLink({
  propertyId,
  size = 'md',
  className,
  mode = 'text',
}: {
  propertyId: string;
  size?: 'sm' | 'md';
  className?: string;
  mode?: 'text' | 'icon';
}): JSX.Element {
  if (mode === 'icon') {
    return (
      <PropertyActionIconButton label="Open property">
        <Link
          to={`/dashboard/properties/${propertyId}`}
          aria-label="Open property"
          className={clsx(propertyActionIconClassName, className)}
        >
          <LayersIcon className="h-4 w-4" />
          <span className="sr-only">Open property</span>
        </Link>
      </PropertyActionIconButton>
    );
  }

  const sizeClasses = size === 'sm' ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm';
  return (
    <Link
      to={`/dashboard/properties/${propertyId}`}
      className={clsx(
        'inline-flex items-center justify-center rounded-2xl bg-brand-primary font-semibold text-white shadow-sm transition hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
        sizeClasses,
        className,
      )}
    >
      View property
    </Link>
  );
}

function EditPropertyLink({
  propertyId,
  size = 'md',
  className,
  mode = 'text',
  variant = 'secondary',
}: {
  propertyId: string;
  size?: 'sm' | 'md';
  className?: string;
  mode?: 'text' | 'icon';
  variant?: 'primary' | 'secondary';
}): JSX.Element {
  if (mode === 'icon') {
    return (
      <PropertyActionIconButton label="Edit property">
        <Link
          to={`/dashboard/properties/${propertyId}/edit`}
          aria-label="Edit property"
          className={clsx(propertyActionIconClassName, className)}
        >
          <Pencil2Icon className="h-4 w-4" />
          <span className="sr-only">Edit property</span>
        </Link>
      </PropertyActionIconButton>
    );
  }

  const sizeClasses = size === 'sm' ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm';
  const baseClasses =
    'inline-flex items-center justify-center rounded-2xl font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary';
  const variantClasses =
    variant === 'primary'
      ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
      : 'border border-surface-subtle bg-white text-text-primary/80 hover:bg-surface-muted/30';
  return (
    <Link
      to={`/dashboard/properties/${propertyId}/edit`}
      className={clsx(baseClasses, variantClasses, sizeClasses, className)}
    >
      Edit property
    </Link>
  );
}

function AddUnitLink({
  propertyId,
  size = 'md',
  className,
  mode = 'text',
  variant = 'secondary',
}: {
  propertyId: string;
  size?: 'sm' | 'md';
  className?: string;
  mode?: 'text' | 'icon';
  variant?: 'primary' | 'secondary';
}): JSX.Element {
  const unitCreatePath = `/dashboard/properties/${propertyId}/units/new`;

  if (mode === 'icon') {
    return (
      <PropertyActionIconButton label="Add unit">
        <Link
          to={unitCreatePath}
          aria-label="Add unit"
          className={clsx(propertyActionIconClassName, className)}
        >
          <PlusIcon className="h-4 w-4" />
          <span className="sr-only">Add unit</span>
        </Link>
      </PropertyActionIconButton>
    );
  }

  const sizeClasses = size === 'sm' ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm';
  const baseClasses =
    'inline-flex items-center justify-center rounded-2xl font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary';
  const variantClasses =
    variant === 'primary'
      ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
      : 'border border-surface-subtle bg-white text-text-primary/80 hover:bg-surface-muted/30';

  return (
    <Link to={unitCreatePath} className={clsx(baseClasses, variantClasses, sizeClasses, className)}>
      Add unit
    </Link>
  );
}
